import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HandCoins } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { receivablesService } from '../../../services/receivablesService';
import { salesOrderService } from '../../../services/salesOrderService';
import type { SalesOrder } from '../../../types/salesOrder';
import { useLanguage } from '../../../features/language/useLanguage';
import { formatCurrency } from '../../../utils/format';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { Modal } from '../../../components/common/Modal';
import { useToast } from '../../../components/common/Toast';
import { CreateSalesOrderForm } from '../../sales-orders/CreateSalesOrderForm';
import { CARD_PAGE_SIZE, EmptyState, ReportCard, thBase } from './ReportCard';

interface ReceivablesSectionProps {
  /** End of the report's date range, YYYY-MM-DD. Used as the as-of date. */
  endDate: string;
  currencySymbol: string;
}

const formatDay = (value: string | null, locale: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
};

export const ReceivablesSection: React.FC<ReceivablesSectionProps> = ({ endDate, currencySymbol }) => {
  const { t, languageCode } = useLanguage();
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [viewingOrder, setViewingOrder] = useState<SalesOrder | null>(null);
  const locale = languageCode === 'id' ? 'id-ID' : 'en-GB';

  const openOrder = async (orderId: string) => {
    const order = await salesOrderService.getById(orderId);
    if (!order) {
      showToast(t('reports.receivables.orderLoadFailed'), 'error');
      return;
    }
    setViewingOrder(order);
  };

  // Receivables are a balance, not a flow: what is owed is stated as of one
  // date rather than summed across a period, so only the range's end date
  // takes part. Narrowing by the start date too would hide a March invoice
  // still unpaid in July — the debt that matters most.
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports_receivables', endDate],
    queryFn: () => receivablesService.get(endDate),
  });

  if (isLoading) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-6" data-test-id="receivables-section">
        <LoadingSpinner text={t('reports.receivables.loading')} />
      </section>
    );
  }
  if (error || !data) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-6" data-test-id="receivables-section">
        <p className="text-sm text-red-600">{t('reports.receivables.loadFailed')}</p>
      </section>
    );
  }

  const orders = data.orders;
  const mostOverdue = orders.reduce((max, o) => Math.max(max, o.days_overdue), 0);

  // Client-side paging: the payload is already fully loaded. Clamp the page so
  // a shrinking dataset never lands on an empty one.
  const pages = Math.max(1, Math.ceil(orders.length / CARD_PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const rows = orders.slice((safePage - 1) * CARD_PAGE_SIZE, safePage * CARD_PAGE_SIZE);

  return (
    <section className="space-y-4" data-test-id="receivables-section">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.receivables.totalLabel')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900" data-test-id="receivables-total">
            {formatCurrency(data.total_amount, currencySymbol)}
          </p>
        </div>
        {/* The one aging signal worth reading without opening the table. */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.receivables.mostOverdueLabel')}</p>
          <p
            className={cn('mt-1 text-xl font-semibold', mostOverdue > 0 ? 'text-red-600' : 'text-gray-900')}
            data-test-id="receivables-most-overdue"
          >
            {mostOverdue > 0 ? t('reports.receivables.daysValue', { days: mostOverdue }) : t('reports.receivables.noneOverdue')}
          </p>
        </div>
      </div>

      <ReportCard
        icon={<HandCoins className="h-5 w-5" strokeWidth={2} />}
        title={t('reports.receivables.title')}
        count={orders.length}
        page={safePage}
        total={orders.length}
        onPageChange={setPage}
        note={t('reports.receivables.asOf', { date: formatDay(data.as_of, locale) })}
        testId="receivables-card"
      >
        {orders.length === 0 ? (
          <EmptyState icon={<HandCoins className="h-5 w-5" />} message={t('reports.receivables.empty')} />
        ) : (
          <table className="min-w-full" data-test-id="receivables-table">
            <thead className="bg-gray-50/70">
              <tr>
                <th className={cn(thBase, 'px-4 text-left')}>{t('reports.receivables.table.orderNumber')}</th>
                <th className={cn(thBase, 'text-left')}>{t('reports.receivables.table.customer')}</th>
                <th className={cn(thBase, 'text-left')}>{t('reports.receivables.table.dueDate')}</th>
                <th className={cn(thBase, 'text-right')}>{t('reports.receivables.table.daysOverdue')}</th>
                <th className={cn(thBase, 'px-4 text-right')}>{t('reports.receivables.table.amount')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(o => (
                <tr
                  key={o.id}
                  onClick={() => void openOrder(o.id)}
                  // The row is the control, so it must be reachable and
                  // operable without a mouse — a keyboard user has no other
                  // way into an order from here.
                  tabIndex={0}
                  role="button"
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void openOrder(o.id);
                    }
                  }}
                  className="cursor-pointer transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                  data-test-id={`receivables-row-${o.id}`}
                >
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{o.order_number}</td>
                  <td className="max-w-[11rem] truncate px-3 py-2.5 text-sm text-gray-600" title={o.customer_name}>
                    {o.customer_name || '-'}
                  </td>
                  <td className="px-3 py-2.5 text-sm tabular-nums text-gray-600">{formatDay(o.due_date, locale)}</td>
                  <td
                    className={cn(
                      'px-3 py-2.5 text-right text-sm tabular-nums',
                      o.days_overdue > 0 ? 'font-medium text-red-600' : 'text-gray-400'
                    )}
                  >
                    {o.days_overdue > 0 ? t('reports.receivables.daysValue', { days: o.days_overdue }) : '-'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm font-semibold tabular-nums text-gray-900">
                    {formatCurrency(o.total_amount, currencySymbol)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ReportCard>

      {/* Read-only sales order, same viewer the customer report opens. */}
      <Modal
        isOpen={!!viewingOrder}
        onClose={() => setViewingOrder(null)}
        title={viewingOrder?.orderNumber ?? ''}
        maxWidth="7xl"
      >
        {viewingOrder && (
          <CreateSalesOrderForm
            key={`receivables-so-view-${viewingOrder.id}`}
            isOpen={!!viewingOrder}
            initialData={viewingOrder}
            onSubmit={async () => {}}
            onCancel={() => setViewingOrder(null)}
            readOnly
          />
        )}
      </Modal>
    </section>
  );
};
